import { useState, useEffect } from 'react'
import { supabase } from '../../../shared/lib/supabase'

function MFAVerify({ onVerified, onCancel, isIdleTimeout = false, factorId: propFactorId = null }) {
  const [code, setCode] = useState('')
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const [factorId, setFactorId] = useState(propFactorId)

  // Fetch the user's MFA factor if not provided
  useEffect(() => {
    const fetchFactor = async () => {
      if (propFactorId) return

      try {
        const { data, error } = await supabase.auth.mfa.listFactors()
        if (error) throw error

        // Get the first verified TOTP factor
        const totpFactor = data.totp?.find(f => f.status === 'verified')
        if (totpFactor) {
          setFactorId(totpFactor.id)
        } else {
          setError('No MFA factor found. Please set up MFA first.')
        }
      } catch (err) {
        setError(err.message)
      }
    }

    fetchFactor()
  }, [propFactorId])

  const handleVerify = async (e) => {
    e.preventDefault()

    if (!factorId) {
      setError('No MFA factor available')
      return
    }

    setLoading(true)
    setError(null)

    try {
      // Create a challenge
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId
      })

      if (challengeError) throw challengeError

      // Verify the code
      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challengeData.id,
        code
      })

      if (verifyError) throw verifyError

      // Store the last MFA verification time
      localStorage.setItem('ssa-mfa-verified-at', Date.now().toString())

      // Log MFA verification
      import('../../../shared/lib/auditLog').then(({ logAuditEvent }) =>
        logAuditEvent({
          category: 'system',
          action: isIdleTimeout ? 'mfa.reverify' : 'mfa.verified',
          description: isIdleTimeout
            ? 'User re-verified MFA after idle timeout'
            : 'User completed two-factor authentication'
        })
      )

      onVerified?.()
    } catch (err) {
      if (err.message.includes('Invalid')) {
        setError('Invalid code. Please try again.')
      } else {
        setError(err.message)
      }
      setCode('')
    } finally {
      setLoading(false)
    }
  }

  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, '')
    setCode(value)

    // Auto-submit when 6 digits entered
    if (value.length === 6 && !loading) {
      // Small delay to show the full code
      setTimeout(() => {
        const form = e.target.closest('form')
        if (form) form.requestSubmit()
      }, 100)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900/40 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-primary-600 dark:text-accent-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {isIdleTimeout ? 'Session Verification Required' : 'Two-Factor Authentication'}
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
            {isIdleTimeout
              ? 'Your session has been idle. Please verify your identity to continue.'
              : 'Enter the 6-digit code from your authenticator app.'
            }
          </p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          </div>
        )}

        <form onSubmit={handleVerify} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 text-center">
              Authentication Code
            </label>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]*"
              maxLength={6}
              value={code}
              onChange={handleCodeChange}
              placeholder="000000"
              className="w-full p-4 text-center text-3xl font-mono tracking-[0.5em] border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              autoFocus
              disabled={loading || !factorId}
            />
          </div>

          <div className="flex justify-center space-x-3">
            {onCancel && (
              <button
                type="button"
                onClick={onCancel}
                className="btn-secondary"
                disabled={loading}
              >
                {isIdleTimeout ? 'Sign Out' : 'Cancel'}
              </button>
            )}
            <button
              type="submit"
              className="btn-primary"
              disabled={loading || code.length !== 6 || !factorId}
            >
              {loading ? (
                <span className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Verifying...
                </span>
              ) : (
                'Verify'
              )}
            </button>
          </div>
        </form>

        <p className="mt-4 text-center text-xs text-gray-500 dark:text-gray-400">
          Open your authenticator app to view your code
        </p>
      </div>
    </div>
  )
}

export default MFAVerify
